import React, { useEffect, useState, useCallback, useMemo } from "react"
import axios from 'axios'
import { Card, Col, Row, Breadcrumb, Collapse, message, Space, Tabs, Table, Typography, Spin, Input, InputNumber, Select, notification } from "antd"
import { CaretRightOutlined } from '@ant-design/icons'
import { startCase } from "lodash"

import DragUpload from "../Form/DragUpload"
import { getTopupPackage } from '../../utils/topupPackage'
import { formatDateTime } from '../../helpers/date'

import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";

const { Text } = Typography;

const COLUMNS = ["region", "state", "minWeight", "maxWeight", "teleport", "sfeconomy", "sfexd", "ninjavan", "aramex", "redly"];

const auditInfoLabel = {
  updatedBy: "Last Updated By",
  updatedAt: "Last Updated At",
  reason: "Reason"
}

const InternationalPackageCost = () => {
  const { permissions } = usePermissions();
  const routes = [
    { path: "", breadcrumbName: "App" },
    { path: "/international-package/cost", breadcrumbName: "International Package Cost" },
  ];

  const [isLoading, setIsLoading] = useState(false)
  const [availablePackages, setAvailablePackages] = useState([])
  const [packageCosts, setPackageCosts] = useState({})
  const [activeTab, setActiveTab] = useState(null)
  const [regionFilter, setRegionFilter] = useState(null)
  const [stateSearch, setStateSearch] = useState("")
  const [weightFilter, setWeightFilter] = useState(null)
  const [reason, setReason] = useState("")
  const [isAuditLoading, setIsAuditLoading] = useState(false)
  const [auditInfo, setAuditInfo] = useState(null)

  const regionOptions = useMemo(() => {
    const rows = packageCosts[activeTab] || []
    return [...new Set(rows.map((row) => row.region).filter(Boolean))]
  }, [packageCosts, activeTab])

  const filterRows = (rows) => {
    if (!rows) return rows

    const search = stateSearch.trim().toLowerCase()

    return rows.filter((row) => {
      const matchesRegion = !regionFilter || row.region === regionFilter
      const matchesState = !search || String(row.state || "").toLowerCase().includes(search)
      const matchesWeight = weightFilter === null || weightFilter === undefined
        || (Number(row.minWeight) <= weightFilter && weightFilter <= Number(row.maxWeight))

      return matchesRegion && matchesState && matchesWeight
    })
  }

  const fetchPackageCosts = useCallback(async () => {
    if (!activeTab || packageCosts[activeTab]) {
      return
    }

    setIsLoading(true)
    const res = await axios.get(`/api/international-costs/costs`, { params: { package: activeTab } })

    setPackageCosts(prevPackageCosts => ({
      ...prevPackageCosts,
      [activeTab]: res.data.data
    }))

    setIsLoading(false)
  }, [activeTab])

  const fetchLatestCostAudit = useCallback(async () => {
    setIsAuditLoading(true)
    try {
      const res = await axios.get("/api/admin-audit-logs", {
        params: {
          subjectType: "InternationalCost",
          auditType: "ADMIN",
          limit: 1,
        },
      })
      const auditLogs = res.data.data || []
      if (auditLogs.length > 0) {
        const latestAudit = auditLogs[0]
        setAuditInfo({
          updatedBy: latestAudit.updatedBy?.email || latestAudit.updatedBy?.username || "Unknown",
          updatedAt: new Date(latestAudit.createdAt).toLocaleString(),
          reason: latestAudit.changes?.reason || "No reason provided",
        })
      } else {
        setAuditInfo({
          updatedBy: "N/A",
          updatedAt: "N/A",
          reason: "No cost updates recorded yet",
        })
      }
    } catch (error) {
      message.error("Failed to fetch cost audit history")
      console.error(error)
    } finally {
      setIsAuditLoading(false)
    }
  }, [])

  const handleUploadSuccess = () => {
    setReason("");
    notification.open({
      message: 'Processing',
      description: "Please check the progress in System -> Jobs",
      duration: 0,
      style: { width: 500 }
    });
  }

  useEffect(() => {
    const getAvailablePackages = async () => {
      const response = await axios.get(`/api/international-costs`)
      const packages = response.data.meta?.packages || []

      setAvailablePackages(packages)
      setActiveTab(packages[0] || null)
    }

    getAvailablePackages()
  }, [])
  useEffect(() => { fetchPackageCosts() }, [activeTab, fetchPackageCosts])

  useEffect(() => {
    fetchLatestCostAudit()
  }, [fetchLatestCostAudit])

  return (
    <div>
      <div className="header">
        <Row>
          <Col span={12}>
            <h2 className="detail-header-text">International Package Cost</h2>
          </Col>
          <Col span={12}>
            <Breadcrumb
              style={{ margin: "16px 0", textAlign: "right" }}
              routes={routes}
            />
          </Col>
        </Row>
      </div>

      <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
        {permissions?.includes(PERMISSIONS.UPLOAD_PRICING) && (
          <div className="upload">
            <Collapse
              expandIcon={({ isActive }) => (
                <CaretRightOutlined rotate={isActive ? 90 : 0} />
              )}
            >
              <Collapse.Panel header="Upload New Cost" key="upload">
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Text strong>Reason for cost update: </Text>
                    <Row>
                      <Input.TextArea
                        label="Reason for Cost Update"
                        name="reason"
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        placeholder="Enter reason for cost update"
                        style={{ marginBottom: 16, width: "100%" }}
                        rows={2}
                      />
                    </Row>
                  </Space>
                  <DragUpload
                    action={`${process.env.REACT_APP_SERVER_URL}/api/international-costs/cost`}
                    accept=".xls, .xlsx"
                    method="put"
                    data={{ reason }}
                    beforeUpload={() => {
                      if (!reason.trim()) {
                        message.error("Please enter a reason for the cost update.");
                        return Promise.reject();
                      }
                      return Promise.resolve();
                    }}
                    onUploadSuccess={handleUploadSuccess}
                  />
                </Space>
              </Collapse.Panel>
            </Collapse>
          </div>
        )}

        {permissions.includes(PERMISSIONS.VIEW_ADMIN_AUDIT_LOGS)
          && !isAuditLoading
          && auditInfo
          && (
          <Card>
            {Object.entries(auditInfoLabel).map(([key, label]) => (
              <Row key={key}>
                <span>
                  <Text strong>{label}:{` `} </Text> {auditInfo[key]}
                </span>
              </Row>
            ))}
          </Card>
        )}

        <Collapse
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
        >
          <Collapse.Panel header="Filters" key="filters">
            <Row gutter={12} align="middle">
              <Col>
                <Select
                  showSearch
                  placeholder="Jump to package"
                  style={{ width: 200 }}
                  value={activeTab}
                  onChange={setActiveTab}
                  optionFilterProp="children"
                >
                  {availablePackages.map((type) => (
                    <Select.Option key={type} value={type}>{getTopupPackage(type).label}</Select.Option>
                  ))}
                </Select>
              </Col>
              <Col>
                <Select
                  showSearch
                  allowClear
                  placeholder="Filter by region"
                  style={{ width: 160 }}
                  value={regionFilter}
                  onChange={setRegionFilter}
                >
                  {regionOptions.map((region) => (
                    <Select.Option key={region} value={region}>{region}</Select.Option>
                  ))}
                </Select>
              </Col>
              <Col>
                <Input.Search
                  placeholder="Filter by state"
                  allowClear
                  onSearch={setStateSearch}
                  style={{ width: 180 }}
                />
              </Col>
              <Col>
                <InputNumber
                  placeholder="Weight (kg)"
                  min={0}
                  step={0.1}
                  value={weightFilter}
                  onChange={setWeightFilter}
                  style={{ width: 140 }}
                />
              </Col>
            </Row>
          </Collapse.Panel>
        </Collapse>

        <div className="table">
          <Tabs activeKey={activeTab} animated onTabClick={setActiveTab}>
            {availablePackages.length && availablePackages.map((type) => {
              const columns = COLUMNS.map((columnRaw) => {
                const col = String(columnRaw).toLowerCase();
                return {
                  title: <span>{startCase(columnRaw)}</span>,
                  dataIndex: columnRaw,
                  width: col === "region" ? 250
                    : ["state", "sfexd", "redly", "aramex", "teleport", "ninjavan"].includes(col) ? 80
                      : 110,
                };
              });
              columns.push({
                title: "Uploaded",
                dataIndex: "uploadedAt",
                width: 130,
                render: (text) => formatDateTime(text),
              });

              return (
                <Tabs.TabPane tab={getTopupPackage(type).label} key={type}>
                  <Table
                    rowKey="Id"
                    className="table-section"
                    columns={columns}
                    dataSource={filterRows(packageCosts[type])}
                    loading={isLoading}
                    pagination={{ pageSize: 50 }}
                    scroll={{ x: "max-content" }}
                  />
                </Tabs.TabPane>
              );
            })}
          </Tabs>

          {!availablePackages.length && <Spin size="large" />}
        </div>
      </Space>
    </div>
  )
}

export default InternationalPackageCost;
